import { createSlice } from '@reduxjs/toolkit';
import axios from '../../services/authService';
import { API_ENDPOINTS } from '../../config/apiConfig';

export const createDevicesSlice = createSlice({
  name: 'createDevices',
  initialState: {
    loading: false,
    response: {},
    error: null,
  },
  reducers: {
    reset: (state) => {
      state.loading = false;
      state.response = {};
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setResponse: (state, action) => {
      state.response = action.payload;
      state.loading = false;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
});

export const { reset, setLoading, setResponse, setError } = createDevicesSlice.actions;

/**
 * Create a new device in the database
 * @param {Object} deviceData - Device data
 * @param {string} deviceData.name - Device name
 * @param {string} deviceData.type - Device type ('iOS' or 'Android')
 * @param {string} deviceData.device_id_1 - IDFA (iOS) or GAID (Android)
 * @param {string} [deviceData.device_id_2] - IDFV (iOS only), null for Android
 * @param {string} [deviceData.device_id_3] - Serial number for Appium device identification
 */
export const createDeviceAsync = (deviceData) => async (dispatch, getState) => {
  dispatch(reset());
  dispatch(setLoading(true));

  try {
    // Get current market from Redux state or localStorage
    const reduxMarket = getState().marketSelection?.value;
    const localStorageMarket = localStorage.getItem('market')
      ? JSON.parse(localStorage.getItem('market'))
      : null;
    const currentMarket = reduxMarket || localStorageMarket;

    // Require a market to be selected
    if (!currentMarket?.id) {
      throw new Error('Please select a market before creating a device');
    }

    const marketId = currentMarket.id;

    const payload = {
      market_id: marketId,
      name: deviceData.name,
      type: deviceData.type,
      device_id_1: deviceData.device_id_1,
      device_id_2: deviceData.device_id_2 || null,
      device_id_3: deviceData.device_id_3 || null,
    };

    const response = await axios.post(API_ENDPOINTS.INSERT_DEVICE, payload);

    dispatch(
      setResponse({
        message: 'Device created successfully',
        data: response.data,
      }),
    );

    return { success: true, data: response.data };
  } catch (err) {
    const errorMessage = err.response?.data?.message || err.message || 'Failed to create device';
    console.error('Error creating device:', errorMessage);
    dispatch(setError(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Selectors
export const selectCreateDeviceLoading = (state) => state.createDevices.loading;
export const selectCreateDeviceResponse = (state) => state.createDevices.response;
export const selectCreateDeviceError = (state) => state.createDevices.error;

export default createDevicesSlice.reducer;
